import { forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from '@/hooks/useAuth';

const CTASection = forwardRef<HTMLElement>((props, ref) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleStart = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  return (
    <section ref={ref} className="py-20 sm:py-32 relative">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[128px]" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-electric-purple/10 rounded-full blur-[128px]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative">
        <div className="glass rounded-3xl p-8 sm:p-12 lg:p-16 text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Start Today
          </div>

          {/* Headline */}
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Ready to Start <span className="text-gradient">Winning</span>?
          </h2>

          {/* Subheadline */}
          <p className="text-muted-foreground text-lg sm:text-xl max-w-2xl mx-auto mb-8">
            Join hundreds of sales professionals who are practicing smarter.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="glass" 
              size="lg" 
              onClick={handleStart}
              className="group text-base bg-primary/20 hover:bg-primary/30 text-primary border-primary/30 hover:border-primary/50 shadow-lg shadow-primary/10 hover:shadow-primary/20"
            >
              Start Winning
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button 
              variant="glass" 
              size="lg" 
              onClick={() => navigate('/pricing')}
              className="text-base"
            >
              View Pricing
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="mt-8 pt-8 border-t border-border/50 flex flex-wrap justify-center gap-6 sm:gap-12 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary" />
              Professional rubric scoring
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-accent" />
              Instant feedback
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

CTASection.displayName = 'CTASection';

export default CTASection;
